import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  FieldWrapper,
  FormFieldsContainer,
  GroupedPlansDropdown,
  Label,
  OptionKey,
  RegularField,
  useForm,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import { usePlanTemplatesQuery, usePlanTypesQuery } from '@/modules/plan/queries';

import { usePlanTemplateStore } from './stores';

const REQUIRED_TEXT = 'This field is required';

export const FIELDS = {
  PARENT_TEMPLATE: 'parentTemplate',
  TEMPLATE_NAME: 'templateName',
  PLAN_TYPE: 'planType',
} as const;

export interface PlanTemplateFormValues {
  parentTemplate: string[];
  templateName: string;
  planType: string;
}

export interface PlanTemplateFormRef {
  handlePreSubmit: () => boolean;
  getValues: () => PlanTemplateFormValues;
}

const StyledFieldsList = styled(Box)`
  > div:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacings.spacing16};
  }
`;

const InputWrapper = styled(Box)`
  > div {
    width: 320px;
  }
`;

interface Props {
  parentId?: string;
  testId?: string;
  formRef?: React.RefObject<PlanTemplateFormRef | null>;
}

const PlanTemplateForm: React.FC<Props> = ({ parentId, testId = '', formRef }) => {
  const canCreateMasterTemplate = useHasAccess([{ permission: Permissions.CREATE_MASTER_TEMPLATE }]);
  const [searchValue, setSearchValue] = useState('');
  const [isErrorShown, setIsErrorShown] = useState(false);
  const { formattedListTemplates, formattedTemplates, isLoading } = usePlanTemplatesQuery(true, searchValue);
  const planTypes = usePlanTypesQuery();
  const showErrors = usePlanTemplateStore((state) => state.showErrors);

  const { control, watch, setValue, setError, formState: { errors }, getValues } = useForm<PlanTemplateFormValues>({
    defaultValues: {
      parentTemplate: parentId ? [parentId] : [],
      templateName: '',
      planType: '',
    },
  });

  const parentTemplateValue = watch(FIELDS.PARENT_TEMPLATE);
  const templateNameValue = watch(FIELDS.TEMPLATE_NAME);
  const planTypeValue = watch(FIELDS.PLAN_TYPE);

  const currentTemplate = useMemo(() => {
    if (parentTemplateValue?.length) {
      return formattedListTemplates?.find((item) => item.key === parentTemplateValue[0]);
    }
    return null;
  }, [parentTemplateValue, formattedListTemplates]);

  useEffect(() => {
    if (currentTemplate) {
      setValue(FIELDS.PLAN_TYPE, currentTemplate.accountType || '');
      if (parentId && !templateNameValue) {
        setValue(FIELDS.TEMPLATE_NAME, currentTemplate.name || '');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate]);

  useEffect(() => {
    if (!canCreateMasterTemplate) {
      setError(FIELDS.PARENT_TEMPLATE, {
        message: parentTemplateValue?.length ? '' : REQUIRED_TEXT,
      });
    }
    setError(FIELDS.TEMPLATE_NAME, {
      message: templateNameValue?.trim() ? '' : REQUIRED_TEXT,
    });
    setError(FIELDS.PLAN_TYPE, {
      message: planTypeValue ? '' : REQUIRED_TEXT,
    });
  }, [parentTemplateValue, templateNameValue, planTypeValue, canCreateMasterTemplate, setError]);

  const handleParentTemplateChange = useCallback((value: OptionKey[]) => {
    setValue(FIELDS.PARENT_TEMPLATE, value as string[]);
  }, [setValue]);

  const shouldShowErrors = isErrorShown || showErrors;

  const handlePreSubmit = useCallback(() => {
    setIsErrorShown(true);
    const hasParentError = !canCreateMasterTemplate && !parentTemplateValue?.length;
    const hasNameError = !templateNameValue?.trim();
    const hasPlanTypeError = !planTypeValue;

    if (hasParentError || hasNameError || hasPlanTypeError) {
      return false;
    }
    return true;
  }, [canCreateMasterTemplate, parentTemplateValue, templateNameValue, planTypeValue]);

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        handlePreSubmit,
        getValues: () => ({
          parentTemplate: getValues(FIELDS.PARENT_TEMPLATE) || [],
          templateName: getValues(FIELDS.TEMPLATE_NAME) || '',
          planType: getValues(FIELDS.PLAN_TYPE) || '',
        }),
      };
    }
  }, [formRef, handlePreSubmit, getValues]);

  return (
    <Box
      background="canvas"
      round="container1Round"
      pad="spacing24"
      data-testid={`${testId}_plan-template-form`}
      border={{ color: 'border1'}}
    >
      <StyledFieldsList>
        <FormFieldsContainer>
          <FieldWrapper>
            <Box direction="row" align="center" justify="between" width="100%">
              <Label name={FIELDS.PARENT_TEMPLATE} showRequireIcon={!canCreateMasterTemplate} isNarrow>
                Parent template
              </Label>
              <ControllerWrapper style={{ flex: 'none' }}>
                <InputWrapper>
                  <Controller
                    name={FIELDS.PARENT_TEMPLATE}
                    control={control}
                    render={() => (
                      <GroupedPlansDropdown
                        testId={`${testId}_parent-template`}
                        options={formattedTemplates}
                        defaultValues={parentTemplateValue?.length ? parentTemplateValue : []}
                        onChange={handleParentTemplateChange}
                        groupTitle="Categories"
                        isSingleMode
                        isLoading={isLoading}
                        onSearch={setSearchValue}
                        searchValue={searchValue}
                        showError={shouldShowErrors && !!errors.parentTemplate?.message}
                      />
                    )}
                  />
                </InputWrapper>
              </ControllerWrapper>
            </Box>
          </FieldWrapper>
        </FormFieldsContainer>

        <FormFieldsContainer>
          <FieldWrapper>
            <Box direction="row" align="center" justify="between" width="100%">
              <Label name={FIELDS.TEMPLATE_NAME} showRequireIcon isNarrow>
                Plan template name
              </Label>
              <ControllerWrapper
                style={{ flex: 'none' }}
                {...(shouldShowErrors && errors.templateName?.message ? {
                  errorText: errors.templateName.message,
                } : {})}
              >
                <InputWrapper>
                  <Controller
                    name={FIELDS.TEMPLATE_NAME}
                    control={control}
                    render={({ field }) => (
                      <RegularField
                        name={FIELDS.TEMPLATE_NAME}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Enter Plan name"
                        isError={shouldShowErrors && !!errors.templateName?.message}
                        editMode
                      />
                    )}
                  />
                </InputWrapper>
              </ControllerWrapper>
            </Box>
          </FieldWrapper>
        </FormFieldsContainer>

        <FormFieldsContainer>
          <FieldWrapper>
            <Box direction="row" align="center" justify="between" width="100%">
              <Label name={FIELDS.PLAN_TYPE} showRequireIcon isNarrow>
                Plan type
              </Label>
              <ControllerWrapper
                style={{ flex: 'none' }}
              >
                <InputWrapper>
                  <Controller
                    name={FIELDS.PLAN_TYPE}
                    control={control}
                    render={({ field }) => (
                      <AutocompleteDropdown
                        testId={`${testId}_plan-type`}
                        name={FIELDS.PLAN_TYPE}
                        onChange={field.onChange}
                        options={planTypes}
                        value={field.value}
                        defaultValue={field.value}
                        placeholder="Select a plan type"
                        disabled={!!currentTemplate}
                        {...(shouldShowErrors && errors.planType?.message ? {
                          errorText: errors.planType.message,
                        } : {})}
                      />
                    )}
                  />
                </InputWrapper>
              </ControllerWrapper>
            </Box>
          </FieldWrapper>
        </FormFieldsContainer>
      </StyledFieldsList>
    </Box>
  );
};

export default PlanTemplateForm;
